/** Calendar-day delta from local start-of-today to renewal date (same semantics as CRM filters). */
export function getDaysUntilContractRenewalDate(raw: string | undefined | null): number | null {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return null;
  const ms = new Date(trimmed).getTime();
  if (Number.isNaN(ms)) return null;
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const d = new Date(ms);
  const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOfDate - startOfToday) / (24 * 60 * 60 * 1000));
}

/** HTML `<input type="date">` value (YYYY-MM-DD) → padded MM/DD/YYYY for CRM API storage. */
export function formatHtmlDateValueToUsStorage(dateInput: string): string {
  if (!dateInput) return '';
  const [year, month, day] = dateInput.split('-');
  if (!year || !month || !day) return '';
  return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
}

/**
 * Normalize a stored CRM date (ISO date prefix, US M/D/YYYY, or parseable string) to padded MM/DD/YYYY
 * so UI compare matches API values (e.g. 2026-01-01 vs 01/01/2026).
 */
export function normalizeClientDateToUsStorage(raw: string | undefined | null): string {
  const t = String(raw || '').trim();
  if (!t) return '';
  const iso = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return `${iso[2]}/${iso[3]}/${iso[1]}`;
  }
  const us = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) {
    return `${us[1].padStart(2, '0')}/${us[2].padStart(2, '0')}/${us[3]}`;
  }
  const parsed = new Date(t);
  if (!Number.isNaN(parsed.getTime())) {
    const m = parsed.getMonth() + 1;
    const d = parsed.getDate();
    const y = parsed.getFullYear();
    return `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
  }
  return t;
}

/** Local calendar date parts for renewal (midnight semantics match day-diff helpers). */
function getRenewalLocalYmd(raw: string): { y: number; m0: number; d: number } | null {
  const us = normalizeClientDateToUsStorage(raw);
  const m = us.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) {
    const month = parseInt(m[1], 10);
    const day = parseInt(m[2], 10);
    const year = parseInt(m[3], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { y: year, m0: month - 1, d: day };
    }
  }
  const ms = new Date(raw).getTime();
  if (Number.isNaN(ms)) return null;
  const dt = new Date(ms);
  return { y: dt.getFullYear(), m0: dt.getMonth(), d: dt.getDate() };
}

/**
 * Whole calendar months from start-of-today through the renewal calendar day (borrow month if day-of-month not reached).
 * e.g. 2026-04-02 → 2028-01-01 ⇒ 20 months ⇒ 1 year 8 months ⇒ label 1.8yr.
 */
export function getCalendarMonthsUntilContractRenewal(raw: string | undefined | null): number | null {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return null;
  const renewal = getRenewalLocalYmd(trimmed);
  if (!renewal) return null;
  const today = new Date();
  const ty = today.getFullYear();
  const tm = today.getMonth();
  const td = today.getDate();
  let months = (renewal.y - ty) * 12 + (renewal.m0 - tm);
  if (renewal.d < td) months -= 1;
  return months;
}

/** e.g. 20 months → 1.8yr (1y + 8mo); 23 months → 1.11yr */
function formatYearsMonthsYrLabel(totalMonths: number): string {
  const y = Math.floor(totalMonths / 12);
  const mo = totalMonths % 12;
  return `${y}.${mo}yr`;
}

/**
 * List/table label: ≤30 days `30D`; &lt;12 calendar months `NMO`; ≥12 months `Y.Myr` (M = extra months 0–11, e.g. 1.8yr).
 * Overdue / no date unchanged.
 */
export function formatCompactRenewalLabelFromDays(days: number | null, rawDate?: string | null): string {
  if (days === null) return '—';
  if (days < 0) return 'Overdue';
  if (days <= 30) return '30D';

  const raw = rawDate != null ? String(rawDate).trim() : '';
  const calMonths = raw ? getCalendarMonthsUntilContractRenewal(raw) : null;

  if (calMonths != null && calMonths > 0) {
    if (calMonths < 12) return `${calMonths}MO`;
    return formatYearsMonthsYrLabel(calMonths);
  }

  if (days <= 390) {
    const mo = Math.min(12, Math.ceil((days - 30) / 30));
    return `${mo}MO`;
  }
  if (days <= 730) return '1.0yr';
  if (days <= 1095) return '2.0yr';
  return '3.0yr';
}

export function formatCompactRenewalLabelFromRawDate(raw: string | undefined | null): string {
  return formatCompactRenewalLabelFromDays(getDaysUntilContractRenewalDate(raw), raw);
}

/** True when the stored renewal calendar day is strictly before local start-of-today. */
export function isContractRenewalOverdue(raw: string | undefined | null): boolean {
  const days = getDaysUntilContractRenewalDate(raw);
  return days !== null && days < 0;
}
